import SinvoiceGoodsItemService from "@/services/SinvoiceGoodsItemService";

const createDefaultQuery = () => ({
	itemCode: "",
	itemName: "",
	page: 1,
	limit: 10,
});

const normalizeQuery = (query = {}) => {
	const merged = {
		...createDefaultQuery(),
		...(query || {}),
	};

	if (!merged.itemCode && merged.productCode) {
		merged.itemCode = merged.productCode;
	}

	return merged;
};

const normalizeTaxPercentage = (value) => {
	if (value === undefined || value === null || String(value).trim() === "") return "";
	if (String(value) === "other") return "other";
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : "";
};

const STANDARD_TAX_VALUES = [-2, -1, 0, 5, 8, 10];

const normalizeString = (value) => String(value || "").trim();

const getTaxState = (value) => {
	if (value === undefined || value === null || String(value).trim() === "") {
		return {
			taxPercentage: "",
			taxPercentageInput: null,
		};
	}

	if (String(value) === "other") {
		return {
			taxPercentage: "other",
			taxPercentageInput: null,
		};
	}

	const normalizedValue = normalizeString(value);
	const numeric = Number(normalizedValue);
	if (Number.isFinite(numeric)) {
		if (STANDARD_TAX_VALUES.includes(numeric)) {
			return {
				taxPercentage: numeric,
				taxPercentageInput: null,
			};
		}
		return {
			taxPercentage: "other",
			taxPercentageInput: numeric,
		};
	}

	return {
		taxPercentage: normalizedValue,
		taxPercentageInput: null,
	};
};

const computeTaxAmount = (baseAmount, taxPercentage, taxPercentageInput) => {
	const amount = Number(baseAmount) || 0;
	if (!amount) return 0;

	if (String(taxPercentage) === "other") {
		const custom = Number(taxPercentageInput);
		if (!Number.isFinite(custom) || custom < 0) return 0;
		return Math.round((amount * custom) / 100);
	}

	const numericTax = Number(taxPercentage);
	if (!Number.isFinite(numericTax) || numericTax < 0) return 0;
	return Math.round((amount * numericTax) / 100);
};

const mapApiItem = (item = {}) => {
	const quantity = item.quantity !== undefined && item.quantity !== null && String(item.quantity).trim() !== ""
		? Number(item.quantity)
		: 1;
	const unitPrice = item.unitPrice !== undefined && item.unitPrice !== null && String(item.unitPrice).trim() !== ""
		? Number(item.unitPrice)
		: 0;
	const taxState = getTaxState(item.taxPercentage);
	const itemTotalAmountWithoutTax = quantity * unitPrice;

	return {
		...item,
		id: item.id,
		itemCode: item.itemCode || item.productCode || "",
		productCode: item.itemCode || item.productCode || "",
		code: item.itemCode || item.productCode || "",
		itemName: item.itemName || "",
		itemNote: item.itemNote || "",
		quantity,
		unitPrice,
		unitName: item.unitName || "",
		taxPercentage: taxState.taxPercentage,
		taxPercentageInput: taxState.taxPercentageInput,
		itemTotalAmountWithoutTax,
		taxAmount: computeTaxAmount(itemTotalAmountWithoutTax, taxState.taxPercentage, taxState.taxPercentageInput),
	};
};

const extractPayload = (response) => {
	if (response && response.data && response.data.data !== undefined) {
		return response.data.data;
	}
	return response && response.data ? response.data : {};
};

export const sinvoiceGoodsStore = {
	namespaced: true,
	state: {
		list: [],
		totalElements: 0,
		loading: false,
		lastQuery: createDefaultQuery(),
		loadedOnce: false,
		loadedFullOnce: false,
	},
	getters: {
		list: (state) => state.list,
		totalElements: (state) => state.totalElements,
		loading: (state) => state.loading,
		lastQuery: (state) => state.lastQuery,
		loadedOnce: (state) => state.loadedOnce,
		invoiceTemplateItems: (state) => {
			return (state.list || []).map((item) => {
				const unitPrice = Number(item.unitPrice) || 0;
				const quantity = item.quantity !== undefined && item.quantity !== null && String(item.quantity).trim() !== ""
					? Number(item.quantity)
					: 1;
				const itemTotalAmountWithoutTax = quantity * unitPrice;
				return {
					selection: 1,
					itemCode: item.itemCode || item.productCode || null,
					code: item.itemCode || item.productCode || null,
					itemName: item.itemName || "",
					unitName: item.unitName || "",
					quantity,
					quantityDisplay: Number(quantity.toFixed(2)).toString(),
					unitPrice,
					unitPriceDisplay: new Intl.NumberFormat("de-DE").format(unitPrice),
					itemTotalAmountWithoutTax,
					taxPercentage: normalizeTaxPercentage(item.taxPercentage),
					taxPercentageInput:
						item.taxPercentageInput !== undefined &&
						item.taxPercentageInput !== null &&
						String(item.taxPercentageInput).trim() !== ""
							? Number(item.taxPercentageInput)
							: null,
					taxAmount: Number(item.taxAmount) || 0,
					itemNote: item.itemNote || "",
					allowQuantity: true,
					allowUnitPrice: true,
					allowProduct: true,
					allowUnitName: true,
					allowTax: true,
					isIncreaseItem: true,
					excludeFromTotals: false,
				};
			});
		},
	},
	mutations: {
		SET_LOADING(state, status) {
			state.loading = !!status;
		},
		SET_SEARCH_RESULT(state, payload = {}) {
			state.list = Array.isArray(payload.list) ? payload.list : [];
			state.totalElements = Number(payload.totalElements) || 0;
			state.lastQuery = normalizeQuery(payload.query);
			state.loadedOnce = true;
		},
		SET_LIST_ONLY(state, list) {
			state.list = Array.isArray(list) ? list : [];
			state.totalElements = state.list.length;
			state.loadedOnce = true;
		},
		SET_FULL_LOADED(state, flag) {
			state.loadedFullOnce = !!flag;
		},
		CLEAR_LIST(state) {
			state.list = [];
			state.totalElements = 0;
			state.lastQuery = createDefaultQuery();
			state.loadedOnce = false;
			state.loadedFullOnce = false;
		},
	},
	actions: {
		async searchGoodsItems({ commit }, query = {}) {
			const finalQuery = normalizeQuery(query);
			commit("SET_LOADING", true);
			try {
				const response = await SinvoiceGoodsItemService.searchGoodsItems(finalQuery);
				const data = extractPayload(response) || {};
				commit("SET_SEARCH_RESULT", {
					list: Array.isArray(data.content) ? data.content.map(mapApiItem) : [],
					totalElements: Number(data.totalElements) || 0,
					query: finalQuery,
				});
				return response;
			} finally {
				commit("SET_LOADING", false);
			}
		},
		async loadGoodsTemplateItems({ commit }) {
			const query = { itemCode: "", itemName: "", page: 1, limit: 1000 };
			commit("SET_LOADING", true);
			try {
				const response = await SinvoiceGoodsItemService.searchGoodsItems(query);
				const data = extractPayload(response) || {};
				const list = Array.isArray(data.content) ? data.content.map(mapApiItem) : [];
				commit("SET_LIST_ONLY", list);
				return list;
			} finally {
				commit("SET_LOADING", false);
			}
		},

		// Load full list from BE (preferred when number of items per school is small)
		async loadAllGoodsTemplateItems({ commit }) {
			commit("SET_LOADING", true);
			try {
                const response = await SinvoiceGoodsItemService.getAllGoodsItems();
				// response may be an array or wrapped in response.data.data or response.data.data.content
				let raw = null;
				if (Array.isArray(response)) raw = response;
				else if (response && response.data && response.data.data !== undefined) raw = response.data.data;
				else if (response && response.data) raw = response.data;
				let list = [];
				if (Array.isArray(raw)) {
					list = raw.map(mapApiItem);
				} else if (raw && Array.isArray(raw.content)) {
					list = raw.content.map(mapApiItem);
				}
				commit("SET_LIST_ONLY", list);
				commit("SET_FULL_LOADED", true);
				return list;
			} finally {
				commit("SET_LOADING", false);
			}
		},
		async ensureGoodsTemplateItems({ state, dispatch }) {
			if (state.loadedOnce && Array.isArray(state.list) && state.list.length > 0) {
				return state.list;
			}
			return dispatch("loadGoodsTemplateItems");
		},
		async getGoodsItemDetail(_, id) {
			const response = await SinvoiceGoodsItemService.getGoodsItemDetail(id);
			const payload = extractPayload(response);
			if (response && response.data && response.data.data !== undefined) {
				response.data.data = mapApiItem(payload || {});
			}
			return response;
		},
		async createGoodsItem({ dispatch, state }, payload) {
			const response = await SinvoiceGoodsItemService.createGoodsItem(payload);
			await dispatch("searchGoodsItems", state.lastQuery || createDefaultQuery());
			return response;
		},
		async updateGoodsItem({ dispatch, state }, { id, payload }) {
			const response = await SinvoiceGoodsItemService.updateGoodsItem(id, payload);
			await dispatch("searchGoodsItems", state.lastQuery || createDefaultQuery());
			return response;
		},
		async deleteGoodsItem({ dispatch, state }, id) {
			const response = await SinvoiceGoodsItemService.deleteGoodsItem(id);
			await dispatch("searchGoodsItems", state.lastQuery || createDefaultQuery());
			return response;
		},
		clearGoodsStore({ commit }) {
			commit("CLEAR_LIST");
		},
	},
};
